import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Divider, Select, Space, Alert } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, IdcardOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const { Title, Text } = Typography;
const { Option } = Select;

const Register = () => {
  const [form] = Form.useForm();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // 清除所有消息提示
  useEffect(() => {
    message.destroy(); // 清除所有全局消息
    return () => {
      message.destroy(); // 组件卸载时也清除消息
    };
  }, []);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);
      
      // 处理表单数据，确保格式正确
      const { confirmPassword, ...registerData } = values;
      
      // 将 email 字段重命名为 username，因为后端可能期望这个字段名
      const requestData = {
        ...registerData,
        username: registerData.email
      };
      
      console.log('发送注册数据:', requestData);
      
      // 直接使用 axios 调用注册 API
      const response = await axios.post(`${API_BASE_URL}/auth/register`, requestData);
      console.log('注册响应:', response.data);
      
      if (response.data && (response.data.success || response.status === 200)) {
        setSuccess(true);
        message.success('注册成功！');
        // 使用 setTimeout 延迟导航，确保用户能看到成功消息
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        throw new Error(response.data?.message || '注册失败，请稍后重试');
      }
    } catch (error) {
      console.error('注册失败:', error);
      // 尝试提取更详细的错误信息
      let errorMessage = '注册失败，请检查您的信息并重试。';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      message.error(errorMessage);
      // 确保不会显示成功消息
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Card 
        style={{ 
          width: 450, 
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          borderRadius: '10px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>创建新账号</Title>
          <Text type="secondary">请填写以下信息完成注册</Text>
        </div>
        
        {error && (
          <Alert 
            message={error} 
            type="error" 
            showIcon 
            style={{ marginBottom: 24 }} 
          />
        )}
        
        {success && (
          <Alert 
            message="注册成功！正在跳转到登录页面..." 
            type="success" 
            showIcon 
            style={{ marginBottom: 24 }} 
          />
        )}
        
        <Form
          form={form}
          name="register"
          onFinish={onFinish}
          layout="vertical"
          requiredMark="optional"
        >
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入您的姓名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入您的姓名" />
          </Form.Item>
          
          <Form.Item
            name="email"
            label="电子邮箱"
            rules={[
              { required: true, message: '请输入您的电子邮箱' },
              { type: 'email', message: '请输入有效的电子邮箱' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="请输入您的电子邮箱" />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="账号类型"
            rules={[{ required: true, message: '请选择账号类型' }]}
          >
            <Select placeholder="请选择账号类型">
              <Option value="student">学生</Option>
              <Option value="teacher">教师</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码长度不能少于6个字符' }
            ]}
            hasFeedback
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: '请确认您的密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请确认密码" />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              loading={loading}
              style={{ height: '40px' }}
            >
              注册
            </Button>
          </Form.Item>
        </Form>
        
        <Divider plain>已有账号?</Divider>
        
        <div style={{ textAlign: 'center' }}>
          <Link to="/login">返回登录</Link>
        </div>
      </Card>
    </div>
  );
};

export default Register;
